/**
 * 업로드 이미지가 너무 크면(예: 폰으로 찍은 1억 화소 사진) 2단계 원근보정에서 OpenCV 가
 * 메모리 초과로 크래시한다. 긴 변이 maxSide 를 넘는 경우에만 캔버스로 축소한 blob URL 을
 * 돌려주고, 임계값 이하이면 원본 파일의 object URL 을 그대로 반환한다(기존 동작 보존).
 *
 * 모니터 8분할 사진을 카메라별로 잘라 쓰는 용도라 4000px 로 줄여도 타일 화질은 충분하다.
 */
const DEFAULT_MAX_SIDE = 4000;

export async function toProcessableImageUrl(
  file: File,
  maxSide: number = DEFAULT_MAX_SIDE,
): Promise<string> {
  const objectUrl = URL.createObjectURL(file);
  let img: HTMLImageElement;
  try {
    img = await loadImage(objectUrl);
  } catch {
    return objectUrl; // 로드 실패 시 원본 URL 그대로 (기존 흐름 유지)
  }

  const longest = Math.max(img.naturalWidth, img.naturalHeight);
  if (longest <= maxSide) return objectUrl; // 작은 이미지는 손대지 않음 (회귀 방지)

  const scale = maxSide / longest;
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return objectUrl;
  ctx.drawImage(img, 0, 0, w, h);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.92),
  );
  URL.revokeObjectURL(objectUrl);
  if (!blob) return URL.createObjectURL(file); // 인코딩 실패 시 원본
  return URL.createObjectURL(blob);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
